import { EM_DASH } from "constants/index";

import { FC, useContext, VFC } from "react";

import { CalendarToday } from "@material-ui/icons";
import { Button, Paper, portexColor, TextInput } from "@portex-pro/ui-components";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetPartnersRequestQuery } from "api/rest/partners/partnersApi";
import formatAddress from "app/pages/shipments/utils/formatAddress";
import AttachFileFromButtonContainer from "components/file-uploads/AttachFileFromButtonContainer";
import AttachmentsListContainer from "components/file-uploads/AttachmentsListContainer";
import RecipientTags from "components/RecipientTags";
import Text from "components/Text";
import { CargoGroupContext } from "features/cargo-groups/CargoGroupProvider";
import { normalizedCargoGroupSelectors } from "features/cargo-groups/store/cargoGroupsSlice";
import { useCargoGroupsSliceSelector } from "features/cargo-groups/store/cargoGroupsStore";
import { mapPalletSizeStringToPalletType } from "features/cargo-groups/utils/mapPalletSizeStringToPalletType";
import useLDFlag from "hooks/useLDFlag";
import capitalize from "lodash/capitalize";
import startCase from "lodash/startCase";
import { DateTime } from "luxon";
import { mapDriverPreferenceToCopy } from "pages/shipper/utils/mapDriverPreferenceToCopy";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TrailerTypeEnum } from "types/TrailerType";

import StepCollapse from "../../_components/StepCollapse";
import TemplateOption from "../../_components/TemplateOption";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

const getTrailerTypeCopy = (trailerType: TrailerTypeEnum) => {
  switch (trailerType) {
    case TrailerTypeEnum.RGN:
      return "Removable Gooseneck (RGN)";
    case TrailerTypeEnum.NA_PARTNERS_CHOICE:
      return "N/A - Partner's Choice";
    default: {
      return startCase(capitalize(trailerType));
    }
  }
};

const CargoGroupTableView: FC<{ cargoGroupId: number | string }> = (props) => {
  const { cargoGroupId } = props;
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);

  const handlingUnit = useCargoGroupsSliceSelector((state) => {
    return normalizedCargoGroupSelectors.selectById(state.cargoGroupsSlice.normalizedCargoGroups, cargoGroupId);
  });

  const packingUnit = useCargoGroupsSliceSelector((state) => {
    return normalizedCargoGroupSelectors.selectById(
      state.cargoGroupsSlice.normalizedCargoGroups,
      handlingUnit?.packing_units?.[0] ?? "-1"
    );
  });

  if (!handlingUnit) {
    return null;
  }

  const isPalletized = handlingUnit.packaging_type?.includes("PALLETS");
  const count = handlingUnit.count;
  const lengthPerCount = handlingUnit.length_per_count;
  const widthPerCount = handlingUnit.width_per_count;
  const palletType = mapPalletSizeStringToPalletType[`${lengthPerCount}_${widthPerCount}`];

  const itemDescription = isPalletized
    ? `${!!count ? `${count} x ` : ""}${
        palletType
          ? `${t(`shipper:packingTypeMap.${handlingUnit.packaging_type}`)} (${t(
              `shipper:palletTypeMap.${palletType}`
            )})`
          : t("shipper:palletized")
      }`
    : `${!!handlingUnit.count ? `${handlingUnit.count} x ` : ""}${
        handlingUnit.packaging_type
          ? capitalize(handlingUnit.packaging_type).replaceAll(/_/g, " ")
          : t("shipper:floorLoaded")
      }`;

  const packingUnitDescription = !!packingUnit
    ? `${!!packingUnit.count ? `${packingUnit.count} x ` : ""}${t(
        `shipper:packingTypeMap.${packingUnit.packaging_type}`
      )}`
    : EM_DASH;

  return (
    <tr key={cargoGroupId}>
      <td className="p-2">
        <Text size="small" weight="bold">
          {itemDescription}
        </Text>
      </td>
      <td className="p-2">
        <Text size="small" weight="bold">
          {handlingUnit.length_per_count && handlingUnit.width_per_count
            ? `${handlingUnit.length_per_count} x ${handlingUnit.width_per_count}${
                handlingUnit.height_per_count ? ` x ${handlingUnit.height_per_count}` : ""
              } ${t("common:in")}`
            : EM_DASH}
        </Text>
      </td>
      <td className="p-2">
        <Text size="small" weight="bold">
          {handlingUnit.weight_per_count
            ? // @ts-expect-error this typing is in fact fine
              t(`common:{{value}}${(handlingUnit.weight_unit ?? "LB").toLowerCase()}`, {
                value: (handlingUnit.weight_per_count ?? 1) * (handlingUnit.count ?? 1),
              })
            : EM_DASH}
        </Text>
      </td>
      <td className="p-2">
        <Text size="small" weight="bold">
          {isPalletized ? (handlingUnit.is_stackable ? t("common:yes") : t("common:no")) : EM_DASH}
        </Text>
      </td>
      <td className="p-2">
        <Text size="small" weight="bold">
          {packingUnitDescription ?? EM_DASH}
        </Text>
      </td>
      <td className="p-2">
        <Text size="small" weight="bold">
          {handlingUnit.case_count ?? packingUnit?.case_count ?? EM_DASH}
        </Text>
      </td>
    </tr>
  );
};

const ReviewStep: VFC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);
  const { stepIndex, quoteRequest: quoteRequest } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const releaseQuoteRequestFiles = useLDFlag("releaseQuoteRequestFiles");
  const enableFTLCargoGroups = useLDFlag("ftlCargoGroups");
  const { baseStepCollapseProps } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Review,
    name: t("shipper:reviewAndSend"),
    number: 5,
  });
  const methods = useFormContext<FinalSchema>();
  const { topLevelCargoGroupIds } = useContext(CargoGroupContext);

  const { data: partnerData } = useGetPartnersRequestQuery(
    !!quoteRequest.partners?.length
      ? {
          queryParams: {
            partnerType: "external",
            ids: quoteRequest.partners?.map((partner) => partner.contact_id) ?? [],
          },
        }
      : skipToken
  );
  const { data: teammatesData } = useGetPartnersRequestQuery(
    !!quoteRequest.teammates?.length
      ? {
          queryParams: {
            partnerType: "internal",
            ids: quoteRequest.teammates?.map((teammate) => teammate.contact_id) ?? [],
          },
        }
      : skipToken
  );

  const submit = (data: FinalSchema) => {
    onSubmit(data);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      footer={
        stepIndex <= QuoteStepIndex.Review ? (
          <div className="flex justify-between mt-2 w-full">
            <div />
            <div className="flex justify-between items-center space-x-3">
              <TemplateOption />
              <Button variant="contained" color="primary" size="large" onClick={methods.handleSubmit(submit)}>
                {t("shipper:sendViaPortex")}
              </Button>
            </div>
          </div>
        ) : null
      }
    >
      <div className="flex flex-col w-full mt-6">
        {/* Email Preview Section */}
        <div className="mb-6">
          <Paper variant="outlined" className="bg-gray-50 p-4">
            <div className="flex flex-col space-y-3 mb-4">
              <div className="flex items-center">
                <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.bcc")}:</span>
                <RecipientTags
                  recipients={
                    partnerData?.data.partners.map((partner) => partner.company_name ?? partner.user.email) ?? []
                  }
                  limit={3}
                  customStyle={{ backgroundColor: "#F3F4F5" }}
                />
              </div>
              <div className="flex items-center">
                <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.cc")}:</span>
                <RecipientTags
                  recipients={teammatesData?.data.partners.map((teammate) => teammate.user.email) ?? []}
                  limit={3}
                  customStyle={{ backgroundColor: "#F3F4F5" }}
                />
              </div>
              <div className="flex items-center mb-4">
                <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.subject")}:</span>
                <TextInput value={quoteRequest.email_metadata?.subject} fullWidth className="bg-white" />
              </div>
            </div>

            {!!releaseQuoteRequestFiles && (
              <div className="flex pb-4 justify-end">
                <AttachFileFromButtonContainer />
              </div>
            )}

            <Paper variant="outlined">
              <div className="flex justify-between p-4 items-center border-b-2">
                <div className="flex flex-col">
                  <Text size="large" weight="bold">
                    {t("common:quoteRequest")}
                  </Text>
                  <Text size="small" typographyProps={{ color: "textSecondary" }}>
                    {quoteRequest.portex_id}
                  </Text>
                </div>

                {quoteRequest.deadline_respond_at ? (
                  <div className="bg-orange-100 text-red-500 py-2 px-2 rounded-[8px]">
                    {t("common:deadlineIn{{time}}", {
                      time: DateTime.fromISO(quoteRequest.deadline_respond_at).toRelative(),
                    })}
                  </div>
                ) : (
                  <div />
                )}
              </div>

              <div className="flex">
                <div className="flex flex-col w-1/2 border-r-2 p-4 space-y-4">
                  <Text size="small" weight="bold">
                    {t("shipper:shipmentDetails")}
                  </Text>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("common:mode")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.mode ? t(`common:modeEnum.${quoteRequest.mode}`) : EM_DASH}
                    </Text>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("shipper:numOfTrucks")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.load_spec?.truck_quantity ? quoteRequest.load_spec.truck_quantity : EM_DASH}
                    </Text>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("common:equipment")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.load_spec?.trailer_size
                        ? t(`common:trailerSizes.${quoteRequest.load_spec?.trailer_size}`)
                        : EM_DASH}{" "}
                      {quoteRequest.load_spec?.trailer_type
                        ? getTrailerTypeCopy(quoteRequest.load_spec.trailer_type as TrailerTypeEnum)
                        : EM_DASH}
                    </Text>
                  </div>

                  {enableFTLCargoGroups ? null : (
                    <div className="flex flex-col space-y-1">
                      <Text size="small" typographyProps={{ color: "textSecondary" }}>
                        {t("common:packingType")}
                      </Text>
                      <Text size="small" weight="bold">
                        {quoteRequest.load_spec?.is_palletized ? t("shipper:palletized") : t("shipper:floorLoaded")}
                      </Text>
                    </div>
                  )}

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("shipper:driverPreference")}
                    </Text>
                    <Text size="small" weight="bold">
                      {quoteRequest.load_spec?.driver_preference
                        ? mapDriverPreferenceToCopy[quoteRequest.load_spec?.driver_preference]
                        : EM_DASH}
                    </Text>
                  </div>

                  <div className="flex flex-col space-y-1">
                    <Text size="small" typographyProps={{ color: "textSecondary" }}>
                      {t("common:attachments")}
                    </Text>
                    <Text size="small" weight="bold">
                      <AttachmentsListContainer showDashOnNoAttachments />
                    </Text>
                  </div>

                  {enableFTLCargoGroups && topLevelCargoGroupIds.length > 0 ? (
                    <div className="flex flex-col space-y-2">
                      <Text size="small" weight="bold">
                        {t("common:cargoDetails")}
                      </Text>
                      <table>
                        <thead className="bg-grey-50 border-b-2 p-2">
                          <tr>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                {t("common:item")}
                              </Text>
                            </th>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                {t("shipper:dimensions")}
                              </Text>
                            </th>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                {t("common:totalWeight")}
                              </Text>
                            </th>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                Stackable
                              </Text>
                            </th>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                Packing Unit
                              </Text>
                            </th>
                            <th className="p-2 text-left">
                              <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                {t("common:caseCount")}
                              </Text>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {topLevelCargoGroupIds.map((cargoGroupId) => (
                            <CargoGroupTableView cargoGroupId={cargoGroupId} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-col w-1/2 p-4 space-y-3">
                  <Text size="small" weight="bold">
                    {t("shipper:locations")}
                  </Text>

                  {quoteRequest.stops?.map((stop, index) => (
                    <div key={index} className="flex flex-col space-y-3">
                      <Text
                        size="small"
                        weight="bold"
                        typographyProps={{ style: { color: index === 0 ? portexColor.blue500 : portexColor.green500 } }}
                      >
                        {index === 0
                          ? t("common:pickup")
                          : index === (quoteRequest.stops?.length ?? 0) - 1
                          ? t("common:delivery")
                          : t("common:stopLabel", { no: index })}
                      </Text>
                      {stop.address && (
                        <>
                          <Text size="small" weight="bold">
                            {formatAddress.fromSnakeCase(stop.address, "short")}
                          </Text>
                          <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                            {stop.address?.name && (
                              <>
                                {stop.address?.name}
                                <br />
                              </>
                            )}
                            {formatAddress.fromSnakeCase(stop.address, "long")}
                          </Text>
                          {stop.start && (
                            <div className="flex items-center">
                              <CalendarToday className="mr-2 text-gray-500" style={{ fontSize: 16 }} />
                              <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                {DateTime.fromISO(stop.start, {
                                  zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                                }).toFormat("cccc, MMM d")}
                                {!!stop.start &&
                                !!stop.end &&
                                stop.start === stop.end &&
                                // @ts-expect-error isTimeTbd is possible for creating quote requests
                                (stop.is_time_tbd || stop.isTimeTbd) ? (
                                  <span> {t("shipper:callEmailToSchedule")}</span>
                                ) : (
                                  !!stop.start && (
                                    <span>
                                      {" at "}
                                      {DateTime.fromISO(stop.start, {
                                        zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                                      }).toFormat("h:mm a")}
                                      {!!stop.end && stop.end !== stop.start && (
                                        <span>
                                          {" - "}
                                          {DateTime.fromISO(stop.end, {
                                            zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                                          }).toFormat("h:mm a")}
                                        </span>
                                      )}
                                    </span>
                                  )
                                )}
                              </Text>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Paper>
          </Paper>
        </div>
      </div>
    </StepCollapse>
  );
};

export default ReviewStep;
